"use client";

import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useAnyspendQuote } from "../../../../anyspend/react/hooks/useAnyspendQuote.js";
import { useAnyspendCreateOrder } from "../../../../anyspend/react/hooks/useAnyspendCreateOrder.js";
import { useAnyspendOrderAndTransactions } from "../../../../anyspend/react/hooks/useAnyspendOrderAndTransactions.js";
import { useCreateDepositFirstOrder } from "../../../../anyspend/react/hooks/useCreateDepositFirstOrder.js";
import { useOnOrderSuccess } from "../../../../anyspend/react/hooks/useOnOrderSuccess.js";
import { ALL_CHAINS, RELAY_SOLANA_MAINNET_CHAIN_ID, getAvailableChainIds } from "../../../../anyspend/index.js";
import { getPaymentUrl } from "../../../../anyspend/utils/chain.js";
import { isNativeToken } from "../../../../anyspend/utils/token.js";
import { useAccountWallet, useIsMobile, useSimTokenBalance, useTokenData, useUnifiedChainSwitchAndExecute } from "../../../../global-account/react/index.js";
import { ShinyButton, TextShimmer } from "../../../../global-account/react/index.js";
import { formatTokenAmount } from "../../../../shared/utils/number.js";
import { cn } from "../../../../shared/utils/cn.js";
import { Check, ChevronDown, ChevronsUpDown, Copy, Loader2, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { encodeFunctionData, erc20Abi } from "viem";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useConnectModal } from "thirdweb/react";
import { TokenSelector } from "@relayprotocol/relay-kit-ui";
import { ChainTokenIcon } from "../common/ChainTokenIcon.js";
import { connectModalConfig } from "../common/CryptoPaymentMethod.js";
export function CryptoPayPanel({
  recipientAddress,
  destinationTokenAddress,
  destinationTokenChainId,
  totalAmount,
  buttonText = "Pay",
  themeColor,
  onSuccess,
  onOrderCreated,
  onError,
  callbackMetadata,
  classes,
  senderAddress
}) {
  // Stable refs for callback props to avoid re-triggering effects
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onOrderCreatedRef = useRef(onOrderCreated);
  onOrderCreatedRef.current = onOrderCreated;
  /* ------------------------------------------------------------------ */
  /* Shared state: token selection, quote, balance                      */
  /* ------------------------------------------------------------------ */
  const [selectedSrcChainId, setSelectedSrcChainId] = useState(destinationTokenChainId);
  const [selectedSrcToken, setSelectedSrcToken] = useState(null);
  const [copied, setCopied] = useState(false);
  const {
    address: walletAddress,
    connectedEOAWallet
  } = useAccountWallet();
  const connectedAddress = walletAddress || connectedEOAWallet?.getAccount()?.address;
  const effectiveAddress = connectedAddress || senderAddress;
  const {
    connect: openConnectModal
  } = useConnectModal();
  const {
    data: dstTokenData
  } = useTokenData(destinationTokenChainId, destinationTokenAddress);
  // Default to destination token data once available
  useEffect(() => {
    if (!selectedSrcToken && dstTokenData) {
      setSelectedSrcToken({
        address: destinationTokenAddress,
        chainId: destinationTokenChainId,
        decimals: dstTokenData.decimals || 18,
        symbol: dstTokenData.symbol || "",
        name: dstTokenData.name || "",
        metadata: {
          logoURI: dstTokenData.logoURI || ""
        }
      });
    }
  }, [selectedSrcToken, dstTokenData, destinationTokenAddress, destinationTokenChainId]);
  const isSameToken = selectedSrcToken && selectedSrcToken.address.toLowerCase() === destinationTokenAddress.toLowerCase() && selectedSrcToken.chainId === destinationTokenChainId;
  const {
    anyspendQuote,
    isLoadingAnyspendQuote
  } = useAnyspendQuote({
    type: "swap",
    srcChain: selectedSrcChainId,
    dstChain: destinationTokenChainId,
    srcTokenAddress: selectedSrcToken?.address || "",
    dstTokenAddress: destinationTokenAddress,
    tradeType: "EXACT_OUTPUT",
    amount: totalAmount
  });
  const tokenAddress = selectedSrcToken ? isNativeToken(selectedSrcToken.address) ? "native" : selectedSrcToken.address : undefined;
  const {
    data: balanceData
  } = useSimTokenBalance(effectiveAddress, tokenAddress, selectedSrcChainId);
  const balance = useMemo(() => {
    const b = balanceData?.balances?.[0];
    if (!b?.amount) return {
      raw: BigInt(0),
      formatted: "0",
      decimals: 18
    };
    return {
      raw: BigInt(b.amount),
      formatted: formatTokenAmount(BigInt(b.amount), b.decimals),
      decimals: b.decimals
    };
  }, [balanceData]);
  const srcAmount = useMemo(() => {
    if (isSameToken) return totalAmount;
    return anyspendQuote?.data?.currencyIn?.amount || "0";
  }, [isSameToken, totalAmount, anyspendQuote]);
  const srcAmountFormatted = useMemo(() => {
    if (!selectedSrcToken) return "0";
    return formatTokenAmount(BigInt(srcAmount || "0"), selectedSrcToken.decimals || 18);
  }, [srcAmount, selectedSrcToken]);
  const hasEnoughBalance = balance.raw >= BigInt(srcAmount || "0");
  /* ------------------------------------------------------------------ */
  /* Destination token object (shared by both flows)                    */
  /* ------------------------------------------------------------------ */
  const dstToken = useMemo(() => ({
    address: destinationTokenAddress,
    chainId: destinationTokenChainId,
    decimals: dstTokenData?.decimals || 18,
    symbol: dstTokenData?.symbol || "",
    name: dstTokenData?.name || "",
    metadata: {
      logoURI: dstTokenData?.logoURI || ""
    }
  }), [destinationTokenAddress, destinationTokenChainId, dstTokenData]);
  /* ------------------------------------------------------------------ */
  /* QR / deposit-first order (always created for QR display)           */
  /* ------------------------------------------------------------------ */
  const [qrOrderId, setQrOrderId] = useState();
  const [globalAddress, setGlobalAddress] = useState();
  const qrOrderCreatedRef = useRef(false);
  const {
    createOrder: createDepositOrder,
    isCreatingOrder: isCreatingQrOrder
  } = useCreateDepositFirstOrder({
    onSuccess: data => {
      setQrOrderId(data.data?.id);
      setGlobalAddress(data.data?.globalAddress);
    },
    onError: error => {
      qrOrderCreatedRef.current = false;
      onErrorRef.current?.(error);
    }
  });
  // Create deposit-first order on mount and when token changes
  useEffect(() => {
    if (qrOrderCreatedRef.current) return;
    if (!selectedSrcToken) return;
    qrOrderCreatedRef.current = true;
    createDepositOrder({
      recipientAddress,
      srcChain: selectedSrcChainId,
      dstChain: destinationTokenChainId,
      srcToken: selectedSrcToken,
      dstToken,
      callbackMetadata,
      creatorAddress: effectiveAddress
    });
  }, [selectedSrcToken, selectedSrcChainId, recipientAddress, destinationTokenChainId, dstToken, callbackMetadata, createDepositOrder, effectiveAddress]);
  const {
    orderAndTransactions: qrOat
  } = useAnyspendOrderAndTransactions(qrOrderId);
  useOnOrderSuccess({
    orderData: qrOat,
    orderId: qrOrderId,
    onSuccess: txHash => onSuccess?.({
      orderId: qrOrderId,
      txHash
    })
  });
  // Notify parent when a deposit is detected on the QR/deposit-first order
  const qrDepositNotifiedRef = useRef(false);
  useEffect(() => {
    if (qrOrderId && !qrDepositNotifiedRef.current && qrOat?.data?.depositTxs?.length && qrOat.data.depositTxs.length > 0) {
      qrDepositNotifiedRef.current = true;
      onOrderCreatedRef.current?.(qrOrderId);
    }
  }, [qrOrderId, qrOat?.data?.depositTxs?.length]);
  // QR code value
  const qrAmount = srcAmount && srcAmount !== "0" ? BigInt(srcAmount) : undefined;
  const qrValue = globalAddress && selectedSrcToken ? getPaymentUrl(globalAddress, qrAmount, isNativeToken(selectedSrcToken.address) ? "ETH" : selectedSrcToken.address, selectedSrcChainId, selectedSrcToken.decimals) : "";
  /* ------------------------------------------------------------------ */
  /* Wallet / swap order (created on button click)                      */
  /* ------------------------------------------------------------------ */
  const [walletOrderId, setWalletOrderId] = useState();
  const [isSendingDeposit, setIsSendingDeposit] = useState(false);
  const depositSentRef = useRef(false);
  const {
    switchChainAndExecuteWithEOA
  } = useUnifiedChainSwitchAndExecute();
  const {
    createOrder: createSwapOrder,
    isCreatingOrder: isCreatingSwapOrder
  } = useAnyspendCreateOrder({
    onSuccess: data_0 => {
      const id = data_0.data?.id;
      if (id) setWalletOrderId(id);
    },
    onError: error_0 => {
      setIsSendingDeposit(false);
      onErrorRef.current?.(error_0);
    }
  });
  const {
    orderAndTransactions: walletOat
  } = useAnyspendOrderAndTransactions(walletOrderId);
  // Auto-send deposit tx once swap order is ready
  useEffect(() => {
    if (!walletOat?.data?.order || depositSentRef.current) return;
    const order = walletOat.data.order;
    if (order.status !== "scanning_deposit_transaction") return;
    if (walletOat.data.depositTxs?.length) return;
    depositSentRef.current = true;
    const sendDeposit = async () => {
      try {
        setIsSendingDeposit(true);
        const amount = BigInt(order.srcAmount);
        let txHash_0;
        if (isNativeToken(order.srcTokenAddress)) {
          txHash_0 = await switchChainAndExecuteWithEOA(order.srcChain, {
            to: order.globalAddress,
            value: amount
          });
        } else {
          const data_1 = encodeFunctionData({
            abi: erc20Abi,
            functionName: "transfer",
            args: [order.globalAddress, amount]
          });
          txHash_0 = await switchChainAndExecuteWithEOA(order.srcChain, {
            to: order.srcTokenAddress,
            data: data_1,
            value: BigInt(0)
          });
        }
        if (!txHash_0) {
          // User cancelled or tx failed — reset so they can retry
          depositSentRef.current = false;
          setWalletOrderId(undefined);
          return;
        }
        // Deposit sent — notify parent to transition to order lifecycle tracking
        if (walletOrderId) {
          onOrderCreatedRef.current?.(walletOrderId);
        }
      } catch (error_1) {
        depositSentRef.current = false;
        // Reset order so user can retry
        setWalletOrderId(undefined);
        onErrorRef.current?.(error_1 instanceof Error ? error_1 : new Error(error_1?.message || "Transaction rejected"));
      } finally {
        setIsSendingDeposit(false);
      }
    };
    sendDeposit();
  }, [walletOat, switchChainAndExecuteWithEOA, walletOrderId]);
  useOnOrderSuccess({
    orderData: walletOat,
    orderId: walletOrderId,
    onSuccess: txHash_1 => onSuccess?.({
      orderId: walletOrderId,
      txHash: txHash_1
    })
  });
  const isWaitingForExecution = !!walletOrderId && walletOat?.data?.order.status !== "executed";
  const handleWalletPay = useCallback(() => {
    if (!selectedSrcToken || !connectedAddress) return;
    depositSentRef.current = false;
    setWalletOrderId(undefined);
    createSwapOrder({
      recipientAddress,
      orderType: "swap",
      srcChain: selectedSrcChainId,
      dstChain: destinationTokenChainId,
      srcToken: selectedSrcToken,
      dstToken,
      srcAmount,
      expectedDstAmount: totalAmount,
      callbackMetadata,
      creatorAddress: effectiveAddress
    });
  }, [selectedSrcToken, connectedAddress, effectiveAddress, recipientAddress, selectedSrcChainId, destinationTokenChainId, dstToken, srcAmount, totalAmount, callbackMetadata, createSwapOrder]);
  /* ------------------------------------------------------------------ */
  /* Handlers                                                           */
  /* ------------------------------------------------------------------ */
  const handleSelectToken = token => {
    setSelectedSrcToken(token);
    setSelectedSrcChainId(token.chainId);
    // Reset both order flows
    setQrOrderId(undefined);
    setGlobalAddress(undefined);
    qrOrderCreatedRef.current = false;
    qrDepositNotifiedRef.current = false;
    setWalletOrderId(undefined);
    depositSentRef.current = false;
  };
  const handleCopyAddress = async () => {
    if (globalAddress) {
      await navigator.clipboard.writeText(globalAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  const [shouldAutoPay, setShouldAutoPay] = useState(false);
  const handleConnectWallet = async () => {
    try {
      const wallet = await openConnectModal(connectModalConfig);
      if (wallet) {
        setShouldAutoPay(true);
      }
    } catch (error_2) {
      console.error("Failed to connect wallet:", error_2);
    }
  };
  // Auto-trigger payment after wallet connect
  useEffect(() => {
    if (shouldAutoPay && connectedAddress && selectedSrcToken && hasEnoughBalance && !isLoadingAnyspendQuote) {
      setShouldAutoPay(false);
      handleWalletPay();
    }
  }, [shouldAutoPay, connectedAddress, selectedSrcToken, hasEnoughBalance, isLoadingAnyspendQuote, handleWalletPay]);
  const isLoading = isLoadingAnyspendQuote;
  const isPending = isCreatingSwapOrder || isSendingDeposit || isWaitingForExecution;
  const canPay = connectedAddress && selectedSrcToken && hasEnoughBalance && !isLoading && !isPending;
  const chainName = ALL_CHAINS[selectedSrcChainId]?.name || "the specified chain";
  const chainLogoUrl = ALL_CHAINS[selectedSrcChainId]?.logoUrl;
  // Collapse QR on mobile when a wallet connector is available
  const isMobile = useIsMobile();
  const hasWalletConnector = typeof window !== "undefined" && !!window.ethereum;
  const [qrExpanded, setQrExpanded] = useState(!isMobile || !hasWalletConnector);
  /* ------------------------------------------------------------------ */
  /* Render                                                             */
  /* ------------------------------------------------------------------ */
  return _jsxs("div", {
    className: cn("anyspend-crypto-pay-panel flex flex-col gap-4", classes?.cryptoPanel),
    children: [_jsxs("div", {
      className: "anyspend-token-selector",
      children: [_jsx("label", {
        className: "mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300",
        children: "Pay with"
      }), _jsx(TokenSelector, {
        address: effectiveAddress,
        chainIdsFilter: getAvailableChainIds("from"),
        context: "from",
        fromChainWalletVMSupported: true,
        isValidAddress: true,
        lockedChainIds: getAvailableChainIds("from"),
        multiWalletSupportEnabled: true,
        onAnalyticEvent: undefined,
        popularChainIds: [1, 8453, RELAY_SOLANA_MAINNET_CHAIN_ID],
        setToken: token_0 => {
          handleSelectToken({
            address: token_0.address,
            chainId: token_0.chainId,
            decimals: token_0.decimals,
            metadata: {
              logoURI: token_0.logoURI
            },
            name: token_0.name,
            symbol: token_0.symbol
          });
        },
        supportedWalletVMs: ["evm", "svm"],
        token: undefined,
        trigger: _jsxs("button", {
          className: cn("flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 transition-colors hover:border-gray-300 dark:border-neutral-700 dark:bg-neutral-800 dark:hover:border-neutral-600", classes?.tokenSelector),
          children: [selectedSrcToken ? _jsxs("div", {
            className: "flex items-center gap-3",
            children: [_jsx(ChainTokenIcon, {
              chainUrl: ALL_CHAINS[selectedSrcToken.chainId]?.logoUrl || "",
              tokenUrl: selectedSrcToken.metadata?.logoURI,
              className: "h-8 w-8"
            }), _jsxs("div", {
              className: "text-left",
              children: [_jsx("p", {
                className: "text-sm font-medium text-gray-900 dark:text-gray-100",
                children: selectedSrcToken.symbol
              }), _jsxs("p", {
                className: "text-xs text-gray-500 dark:text-gray-400",
                children: ["Balance: ", balance.formatted]
              })]
            })]
          }) : _jsx("span", {
            className: "text-sm text-gray-400",
            children: "Select token"
          }), _jsx(ChevronsUpDown, {
            className: "h-4 w-4 text-gray-400"
          })]
        })
      })]
    }), _jsx(motion.div, {
      initial: {
        opacity: 0,
        y: 6
      },
      animate: {
        opacity: 1,
        y: 0
      },
      transition: {
        duration: 0.25,
        ease: "easeOut"
      },
      className: cn("rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-neutral-700 dark:bg-neutral-800/50", classes?.quoteDisplay),
      children: _jsxs("div", {
        className: "flex items-center justify-between",
        children: [_jsx("span", {
          className: "text-sm text-gray-500 dark:text-gray-400",
          children: "You pay"
        }), _jsx(AnimatePresence, {
          mode: "wait",
          children: isLoadingAnyspendQuote ? _jsx(motion.div, {
            initial: {
              opacity: 0
            },
            animate: {
              opacity: 1
            },
            exit: {
              opacity: 0
            },
            transition: {
              duration: 0.15
            },
            children: _jsx(TextShimmer, {
              duration: 1,
              className: "text-sm",
              children: "Fetching quote..."
            })
          }, "quote-loading") : _jsxs(motion.span, {
            initial: {
              opacity: 0
            },
            animate: {
              opacity: 1
            },
            exit: {
              opacity: 0
            },
            transition: {
              duration: 0.15
            },
            className: "text-sm font-medium text-gray-900 dark:text-gray-100",
            children: [srcAmountFormatted, " ", selectedSrcToken?.symbol || ""]
          }, "quote-amount")
        })]
      })
    }), _jsx(AnimatePresence, {
      children: connectedAddress && selectedSrcToken && !hasEnoughBalance && !isLoading && _jsxs(motion.p, {
        initial: {
          opacity: 0,
          height: 0
        },
        animate: {
          opacity: 1,
          height: "auto"
        },
        exit: {
          opacity: 0,
          height: 0
        },
        transition: {
          duration: 0.2,
          ease: "easeOut"
        },
        className: "text-center text-sm text-red-500",
        children: ["Insufficient ", selectedSrcToken.symbol, " balance"]
      }, "balance-warning")
    }), !connectedAddress ? _jsx(ShinyButton, {
      accentColor: themeColor || "hsl(var(--as-brand))",
      onClick: handleConnectWallet,
      className: cn("w-full", classes?.payButton),
      textClassName: "text-white",
      children: "Connect Wallet to Pay"
    }) : _jsx(ShinyButton, {
      accentColor: themeColor || "hsl(var(--as-brand))",
      onClick: handleWalletPay,
      disabled: !canPay,
      className: cn("w-full", classes?.payButton),
      textClassName: cn(!canPay ? "text-as-secondary" : "text-white"),
      children: isPending ? _jsxs("span", {
        className: "flex items-center justify-center gap-2",
        children: [_jsx(Loader2, {
          className: "h-4 w-4 animate-spin"
        }), isCreatingSwapOrder ? "Creating order..." : isSendingDeposit ? "Confirm in wallet..." : "Confirming transaction..."]
      }) : buttonText
    }), isMobile && hasWalletConnector ? _jsxs("button", {
      type: "button",
      onClick: () => setQrExpanded(prev => !prev),
      className: "flex w-full items-center gap-3 py-1",
      children: [_jsx("div", {
        className: "h-px flex-1 bg-gray-200 dark:bg-neutral-700"
      }), _jsxs("span", {
        className: "flex items-center gap-1 text-xs font-medium text-gray-400 dark:text-gray-500",
        children: [qrExpanded ? "or send directly" : _jsxs(_Fragment, {
          children: [_jsx(QrCode, {
            className: "h-3 w-3"
          }), " or send with QR code"]
        }), _jsx(ChevronDown, {
          className: cn("h-3 w-3 transition-transform", qrExpanded && "rotate-180")
        })]
      }), _jsx("div", {
        className: "h-px flex-1 bg-gray-200 dark:bg-neutral-700"
      })]
    }) : _jsxs("div", {
      className: "flex items-center gap-3 py-1",
      children: [_jsx("div", {
        className: "h-px flex-1 bg-gray-200 dark:bg-neutral-700"
      }), _jsx("span", {
        className: "text-xs font-medium text-gray-400 dark:text-gray-500",
        children: "or send directly"
      }), _jsx("div", {
        className: "h-px flex-1 bg-gray-200 dark:bg-neutral-700"
      })]
    }), _jsx(AnimatePresence, {
      initial: false,
      children: qrExpanded && _jsx(motion.div, {
        initial: {
          height: 0,
          opacity: 0
        },
        animate: {
          height: "auto",
          opacity: 1
        },
        exit: {
          height: 0,
          opacity: 0
        },
        transition: {
          duration: 0.25,
          ease: "easeInOut"
        },
        className: "overflow-hidden",
        children: isCreatingQrOrder && !globalAddress ? _jsxs("div", {
          className: "flex items-center justify-center py-8",
          children: [_jsx(Loader2, {
            className: "h-5 w-5 animate-spin text-gray-400"
          }), _jsx("span", {
            className: "ml-2 text-sm text-gray-500 dark:text-gray-400",
            children: "Creating deposit address..."
          })]
        }) : globalAddress ? _jsxs("div", {
          className: "flex items-center gap-4",
          children: [_jsx("div", {
            className: "shrink-0 rounded-xl bg-white p-2.5 shadow-sm ring-1 ring-gray-100 dark:bg-white dark:ring-gray-200",
            children: _jsx(QRCodeSVG, {
              value: qrValue,
              size: 132,
              level: "M",
              marginSize: 0
            })
          }), _jsxs("div", {
            className: "flex min-w-0 flex-1 flex-col gap-2.5",
            children: [_jsxs("p", {
              className: "text-sm font-medium text-gray-700 dark:text-gray-300",
              children: ["Send", " ", _jsxs("span", {
                className: "font-semibold text-gray-900 dark:text-gray-100",
                children: [srcAmountFormatted, " ", selectedSrcToken?.symbol]
              }), " ", "on", " ", _jsxs("span", {
                className: "inline-flex items-center gap-1 rounded-full bg-gray-100 px-1.5 py-0.5 align-middle font-semibold text-gray-900 dark:bg-white/10 dark:text-gray-100",
                children: [chainLogoUrl && _jsx("img", {
                  src: chainLogoUrl,
                  alt: "",
                  className: "h-3.5 w-3.5 rounded-full"
                }), chainName]
              }), " ", "to:"]
            }), _jsxs("button", {
              onClick: handleCopyAddress,
              className: "group flex items-start gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-left transition-colors hover:border-gray-300 hover:bg-gray-100 dark:border-neutral-700 dark:bg-neutral-800/60 dark:hover:border-neutral-600 dark:hover:bg-neutral-800",
              children: [_jsx("span", {
                className: "min-w-0 break-all font-mono text-xs leading-relaxed text-gray-800 dark:text-gray-200",
                children: globalAddress
              }), _jsx("span", {
                className: "mt-0.5 shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-300",
                children: copied ? _jsx(Check, {
                  className: "h-3.5 w-3.5 text-green-500"
                }) : _jsx(Copy, {
                  className: "h-3.5 w-3.5"
                })
              })]
            }), _jsxs("p", {
              className: "text-xs leading-snug text-orange-500/80 dark:text-orange-400/80",
              children: ["Only send ", selectedSrcToken?.symbol, " on ", _jsx("span", {
                className: "font-semibold",
                children: chainName
              }), ". Sending other tokens or using a different network may result in loss of funds."]
            })]
          })]
        }) : null
      }, "qr-section")
    })]
  });
}